import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Stage } from '../stage/stage';
import { SiteList } from "../site-list/site-list";
import { Player } from "../player/player";
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import { CameraTimeline, Motion, TimelineSegmentConstraints, TrimSelection } from '../../interfaces/timeline-segments';
import { PlaybackEvents } from '../../interfaces/playback-events';
import { Camera } from '../../interfaces/camera';

@Component({
  standalone: true,
  selector: 'lib-playback-page',
  imports: [Stage, SiteList, Player],
  templateUrl: './playback-page.html',
  styleUrl: './playback-page.scss'
})
export class PlaybackPageComponent implements OnInit, OnDestroy {
  /**
   * For Destroying the subs
   */
  private destroy$ = new Subject<void>();

  /**
   * Direct access to child
   */
  @ViewChild(Stage) stage!: Stage;
  /**
   * List of cameras
   */
  @Input() cameraList!:Camera[];
  /**
   * Data for the motion events to form the timeline
   */
  @Input() cameras: CameraTimeline[] = [];
  /**
   * Control the max vw of the timeline (player)
   */
  @Input() maximumViewPortWidth!: string;
  /**
   * Dynamically pass what motion values we need
   * label is the Name to be displayed
   * value is the value to be mapped with the CameraTimeline name
   * Color is the color intended to be seen on the player for the events
   */
  @Input() motionOptions!:Motion[];

  /**
   * Receive a new list of Camera[] from the app component with selected property changed to
   * track what camera from the list are selected
   */
  @Input() updateSelectedCameras$!: BehaviorSubject<Camera[]>;

  @Input() fullScreenLib$!: BehaviorSubject<boolean>;
  

  /**
   * Start date of the playback
   * OnInit will set now() - 24hrs
   */
  startDate!: Date;
  
  /**
   * End Date of the playback
   */
  endDate!: Date;
 
  /**
   * Boolean to show the site list container
   */
  @Input() siteListActive: boolean = false;
  /**
   * Event to notify that the site list container has been closed
   */
  @Output() siteListCloseEmitter: EventEmitter<boolean> = new EventEmitter();

  /**
   * Event Emitter to relay fetching segment request out to the app component
   */
  @Output() requestSegments = new EventEmitter<PlaybackEvents['requestSegments']>();

  /**
   * Event Emitter to relay segment url to be downloaded to the app component
   */
  @Output() OnDownloadSegmentRequested = new EventEmitter<TrimSelection>();


  /**
   * Selected Camera[]
   */
  selectedCameras:Camera[] = [];

  /**
   * boolean to send an update to site list for updating the list
   */
  searchList$ = new BehaviorSubject<boolean>(false);

  /**
   * video current time
   * Relayed from the stage to the player
   */
  videoTime: Date | null = null;

  /**
   * Set the start and end date to get an interval for the past 24 hours
   */
  constructor(){
    this.endDate = new Date();
    this.endDate.setMinutes(0, 0, 0);
    this.endDate.setHours(this.endDate.getHours() + 1);

    this.startDate = new Date(this.endDate.getTime() - 24 * 60 * 60 * 1000);
    this.startDate.setMinutes(0, 0, 0);
    this.startDate.setHours(this.startDate.getHours() - 1);
  }


  /**
   * Initialize selected Cameras 
   * Subscribe to the camera update event to
   * update the selected cameras
   */
  ngOnInit(): void {
    this.selectedCameras = [];
    this.updateSelectedCameras$
    .pipe(takeUntil(this.destroy$))
    .subscribe(newValue => {
      if(!newValue.length) return;
      this.cameraList = newValue;
        this.selectedCameras = [];      
      this.selectedCameras.push(...this.cameraList.filter((el:Camera)=> el.selected));


    })
      this.fullScreenLib$
      .pipe(takeUntil(this.destroy$))
      .subscribe(enable=> {
        if(enable){
          this.stage?.enterFullScreen();
        }
      })
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
}

  /**
   * Event Listener for the state of site list
   * @param {boolean }value is the site list open/close
   */
  onSiteListClose(value:boolean){
    this.siteListActive = false;
    this.siteListCloseEmitter.emit(value);
  }

  /**
   * Remove the camera from the site list and mark selected property
   * @param {Camera} removed 
   * @returns 
   */
  findAndRemoveSelected(removed: Camera){
    const currentEl = this.cameraList.find(
      (el: Camera) => el.id === removed.id
    );

    if (!currentEl) return;

    currentEl.selected = false;
  }

  /**
   * receive the event from the lib site list component 
   * to update the selected camera list
   * selectedCameras:Camera[] are also passed to
   * the stage to control the video components 
   * based on the selection from site list
   * @param {{type: String, camera: Camera}} value 
   */
  onSiteSelectionUpdate(value:{type: String, camera:Camera}){
    let findMatchingCamera = this.cameraList.find((el:Camera)=> el.id === value.camera.id);
    if(value.type === 'Selection'){
      if(this.selectedCameras.length >=4){
        const shiftedEl:Camera | undefined = this.selectedCameras.shift();
        if(!shiftedEl) return;
        this.findAndRemoveSelected(shiftedEl);
        
      }
      
      
      if(findMatchingCamera){
        findMatchingCamera.selected = true;
        this.selectedCameras.push(value.camera);
      }
    }
    else{
       const removed = value.camera;
       this.findAndRemoveSelected(removed);
       this.selectedCameras = this.selectedCameras.filter(el=> el.id !== removed.id);
       if(findMatchingCamera){
        findMatchingCamera.selected = false;
       }
    }
    this.searchList$.next(true);
      if (this.selectedCameras.length) {
    this.requestSegments.emit({
      cameraIds: this.selectedCameras.map(c => c.id),
      start: this.startDate,
      end: this.endDate
    });
  }

  }


/**
 * Event hooked up to the player component
 * Triggers once the 24-72hrs slider value is changed
 * Emits the requestSegments to fetch the segments from the parent for the time period
 * and the selected motion events
 * @param {TimelineSegmentConstraints} $event 
 */
onTimeLineUpdate($event:TimelineSegmentConstraints){
   const {start,end} = $event;
   this.startDate = start;
   this.endDate = end;
   this.requestSegments.emit({
      cameraIds: this.selectedCameras.map(c => c.id),
      start: this.startDate,
      end: this.endDate
    });

}


/**
 * Event Emitter to send the event to the parent for handling download
 * @param {TrimSelection} event 
 */
onDownloadSegment(event: TrimSelection){
  this.OnDownloadSegmentRequested.emit(event);
}

/**
 * Relay the time from the stage to the player
 * @param {{cameraId : number, currentTime: Date}} event 
 */
onVideoTimeUpdate(event: {
  cameraId: number;
  currentTime: Date;
}) {
  this.videoTime = event.currentTime;
}
}
